import { DispatchDate } from '@utils/dispatch';
import { framedByDefault } from '@utils/frames';
import {
  isStoryblokRichTextPresent,
  renderStoryblokRichText,
} from '@utils/renderStoryblokRichText';
import { useTranslation } from 'next-i18next';

import {
  presentedPurchasingState,
  purchasingState,
} from '../../../helpers/purchasing';
import { useUnitOfMeasurement } from '../../../hooks/useUnitOfMeasurement';
import {
  MultiassetStoryblok,
  ProductDimensionsStoryblok,
  ProductV2Storyblok,
  RichtextStoryblok,
} from '../../../types/generated-storyblok';
import { ProductDispatchDate } from '../../ProductDispatchDate/ProductDispatchDate';
import { getActiveDimensionsLabel } from '../Dimensions';
import { ProductEditionSize } from './ProductEditionSize';
import { ProductInfoLabel } from './ProductInfoLabel';

type ProductReleaseInfoProps = {
  // purchasing controller
  available?: boolean;
  tags?: string[];
  freeShipping?: boolean;
  framingOffered?: string | null;
  dispatchDate: DispatchDate;
  //
  // product storyblok
  releaseType: StoryblokReleaseType;
  closingDate?: string;
  releaseNote?: StoryblokTextAreaType | RichtextStoryblok;
  editionNote?: string;
  editionSize?: string | number;
  releaseCollectionGallery?: MultiassetStoryblok;
  medium?: StoryblokDatatypeMedium[] | Array<string | number>;
  editionFormat?: ProductV2Storyblok['editionFormat'];
  hideShippingEstimate: boolean;
  dimensions?: ProductDimensionsStoryblok;
  isFramed?: boolean;
  //
  onEditionSizeAnchorClick?: () => void;
};

export const ProductReleaseInfo = (props: ProductReleaseInfoProps) => {
  const { t } = useTranslation(['product']);
  const [unitMeasurement] = useUnitOfMeasurement();

  const state = presentedPurchasingState(
    purchasingState({
      releaseType: props.releaseType,
      closingDate: props.closingDate,
      available: props?.available,
      tags: props?.tags,
    }),
  );

  const isSoldOut = state === 'SOLD_OUT';
  const isFreeShipping = props?.freeShipping;
  const isHandFinished = props?.medium?.includes('hand');
  const isFrameIncluded = framedByDefault(props?.framingOffered);
  const dimensionsLabel = getActiveDimensionsLabel(
    props.dimensions,
    unitMeasurement,
    props.isFramed ?? false,
  );

  return (
    <div className="flex w-full flex-col gap-y-3 text-center text-neutral-4">
      <div className="flex flex-wrap items-center justify-center gap-2">
        <ProductEditionSize
          editionFormat={props.editionFormat}
          editionNote={props.editionNote}
          editionSize={props.editionSize}
        />

        {dimensionsLabel ? (
          <ProductInfoLabel>{dimensionsLabel}</ProductInfoLabel>
        ) : null}

        {!isSoldOut && isFreeShipping ? (
          <ProductInfoLabel>{t('benefits.shipping.free')}</ProductInfoLabel>
        ) : null}

        {isSoldOut || props.hideShippingEstimate ? null : (
          <ProductDispatchDate
            dispatchDate={props.dispatchDate}
            renderLabel={(label) => (
              <ProductInfoLabel>{label}</ProductInfoLabel>
            )}
            variant="estimation"
          />
        )}

        {isHandFinished ? (
          <ProductInfoLabel>{t('variants.handFinishedYes')}</ProductInfoLabel>
        ) : null}

        {isFrameIncluded ? (
          <ProductInfoLabel>{t('framing.included.title')}</ProductInfoLabel>
        ) : null}
      </div>

      {!!props?.releaseCollectionGallery?.length &&
        props.onEditionSizeAnchorClick && (
          <button
            className="hyperlink text-xs"
            onClick={props.onEditionSizeAnchorClick}
            type="button"
          >
            {t('releaseCollectionGalleryHeading')}
          </button>
        )}

      {isStoryblokRichTextPresent(props.releaseNote) && (
        <div>
          {renderStoryblokRichText(props.releaseNote, {
            paragraphClassName: 'pb-0',
          })}
        </div>
      )}
    </div>
  );
};
